import ShortUniqueId from 'short-unique-id'
import { MANUAL_SPOT_LIBRARY_SUFFIX } from '../constants/agami.constants'
import { SUBMISSION_OUTCOMES } from '../constants/db.constants'

const { randomUUID } = new ShortUniqueId({ length: 16 })

export const createManualCodeLibrarySuffix = (code: string): string | null => {
  if (!code) return null
  // generate unique random ID of 16 charachters
  const randomId = randomUUID()
  // append ot code with fixed suffix
  return `${code}${MANUAL_SPOT_LIBRARY_SUFFIX}${randomId}`
}

export const removeManualCodeLibrarySuffix = (code?: string): string | undefined => {
  if (!code) return
  // Create a regular expression pattern based on the updated schema
  const pattern = new RegExp(`${MANUAL_SPOT_LIBRARY_SUFFIX}[A-Za-z0-9]{16}`)
  // Use replace method to remove the matching part of the string
  const result = code.replace(pattern, '')
  // return
  return result
}

export const checkLibraryManualCode = (code: string): boolean => {
  // Create a regular expression pattern based on the updated schema
  const pattern = new RegExp(`${code}${MANUAL_SPOT_LIBRARY_SUFFIX}[A-Za-z0-9]{16}`)
  return pattern.test(code)
}

export const checkManualCodeInLibrary = ({ code, spots }: { code: string; spots: string[] }): boolean => {
  // Create a regular expression pattern based on the updated schema
  const pattern = new RegExp(`${code}${MANUAL_SPOT_LIBRARY_SUFFIX}[A-Za-z0-9]{16}`)
  // Check if any element in the "spots" array matches the pattern
  const matchingSpot = spots.find((element) => pattern.test(element))
  // Return true if a matching element is found, otherwise return false
  return !!matchingSpot
}

/**
 * Splits a list into published agami and drafts (05.3).
 *
 * ONE logic for both views: if cards and table partitioned on their own, they
 * would diverge at the first change — and the symptom ("the table shows one
 * more draft") would never be traced back to two filters in two files.
 */
export const partitionByDraft = <T extends { is_draft: boolean }>(
  spots: T[]
): { published: T[]; drafts: T[] } => {
  const published: T[] = []
  const drafts: T[] = []

  spots.forEach((spot) => {
    if (spot.is_draft) drafts.push(spot)
    else published.push(spot)
  })

  return { published, drafts }
}

export type AgamiMediaFilter = 'audio' | 'video' | 'none'

export type AgamiSortMode = 'default' | 'name' | 'lastUpdate'

/**
 * Sorts the list loaded for an experience without mutating the hook result.
 * `default` deliberately means "backend order", not "last update": the two
 * happen to coincide today but may diverge when the query evolves.
 */
export const sortAgamiList = <T extends { name: string; updated_at: string }>(
  spots: readonly T[],
  mode: AgamiSortMode,
  locale: string
): T[] => {
  if (mode === 'default') return [...spots]

  const indexed = spots.map((spot, index) => ({ spot, index }))
  const collator = mode === 'name' ? new Intl.Collator(locale, { sensitivity: 'base', numeric: true }) : null

  indexed.sort((left, right) => {
    if (mode === 'name') {
      const comparison = collator!.compare(left.spot.name, right.spot.name)
      return comparison || left.index - right.index
    }

    const leftTime = Date.parse(left.spot.updated_at)
    const rightTime = Date.parse(right.spot.updated_at)
    const leftIsInvalid = Number.isNaN(leftTime)
    const rightIsInvalid = Number.isNaN(rightTime)

    if (leftIsInvalid && rightIsInvalid) return left.index - right.index
    if (leftIsInvalid) return 1
    if (rightIsInvalid) return -1
    return rightTime - leftTime || left.index - right.index
  })

  return indexed.map(({ spot }) => spot)
}

export type AgamiListFilters = {
  /** Activation type id from the spot_types lookup, or null for all. */
  type: number | null
  media: AgamiMediaFilter | null
}

/**
 * Does this agami HAVE a media file? Decided by `audio_content_path`, NOT by
 * the signed URL: a word whose URL failed to generate still has its content
 * (07.2). ONE helper for the indicator and the "no media" filter — if the two
 * answered the question differently, the filtered list would show rows without
 * the indicator and nobody would know which one is right.
 */
export const hasMedia = (spot: { audio_content_path?: string | null }): boolean =>
  Boolean(spot.audio_content_path)

export type AgamiMediaState = 'intentional-none' | 'awaiting-upload' | 'available' | 'unavailable'

export const getAgamiMediaState = (spot: {
  content_media_type?: number
  audio_content_path?: string | null
  audioUrl?: string
}): AgamiMediaState => {
  if (spot.content_media_type === 3) return 'intentional-none'
  if (!hasMedia(spot)) return 'awaiting-upload'
  return spot.audioUrl ? 'available' : 'unavailable'
}

const matchesMediaFilter = (
  spot: { content_media_type?: number; audio_content_path?: string | null },
  media: AgamiMediaFilter
): boolean => {
  if (media === 'video') return spot.content_media_type === 2
  if (media === 'none') return spot.content_media_type === 3
  return spot.content_media_type !== 2 && spot.content_media_type !== 3
}

/**
 * A draft is a PROPOSAL when a pending submission exists for it (D15): no new
 * column, the satellite is the discriminant. Approved submissions stay attached
 * with outcome 'approved' (the flag turns off); rejected ones detach (spot_id
 * goes NULL with the spot deleted). For a non-owner the embed is empty by RLS,
 * so this is simply false.
 */
export const hasPendingSubmission = (spot: {
  experience_draft_submissions?: Array<{ outcome: string }>
}): boolean =>
  Boolean(spot.experience_draft_submissions?.some((s) => s.outcome === SUBMISSION_OUTCOMES.PENDING))

/**
 * What the container attaches to the representative of a proposal group (05.4):
 * pure client-side decoration, never persisted.
 */
export type AgamiProposalMeta = {
  /** Every spot in the group, the representative included: the reject action iterates these. */
  spotIds: string[]
  count: number
  submittedAt: string
  submittedBy: string | null
  /** Resolved by get-proposal-context (profiles is RLS'd to its own user), so it arrives late or never. */
  proposerName?: string
}

type PendingSubmission = {
  outcome: string
  original_title: string
  submitted_at: string
  submitted_by: string | null
  experience_id?: string
}

/** The pending submission that makes a draft a proposal (D15), if any. Exported for the banner of 05.5. */
export const pendingSubmissionOf = <T extends { experience_draft_submissions?: PendingSubmission[] }>(
  spot: T
): PendingSubmission | undefined =>
  spot.experience_draft_submissions?.find((s) => s.outcome === SUBMISSION_OUTCOMES.PENDING)

/**
 * Splits the drafts into working drafts and proposal GROUPS (05.4-T8): pending
 * proposals with the same `original_title` collapse into one entry, so twelve
 * "musica" are one row with a counter and ONE rejection — not twelve dialogs.
 *
 * Grouping works because 03.3 normalizes the title before saving it: without
 * that, "musica" and "Musica " would be two groups. The key is the satellite's
 * `original_title`, not the spot name, which the owner may have started editing.
 *
 * The representative is the most recently submitted of the group, and the group
 * context (who/when) is the representative's: with several proposers the counter
 * already says this entry is a crowd.
 */
export const groupProposals = <T extends { id: string; experience_draft_submissions?: PendingSubmission[] }>(
  drafts: T[]
): { workingDrafts: T[]; proposalGroups: Array<{ representative: T; meta: AgamiProposalMeta }> } => {
  const workingDrafts: T[] = []
  const byTitle = new Map<string, Array<{ spot: T; submission: PendingSubmission }>>()

  drafts.forEach((spot) => {
    const submission = pendingSubmissionOf(spot)
    if (!submission) {
      workingDrafts.push(spot)
      return
    }
    const group = byTitle.get(submission.original_title) ?? []
    group.push({ spot, submission })
    byTitle.set(submission.original_title, group)
  })

  const proposalGroups = Array.from(byTitle.values()).map((members) => {
    const sorted = [...members].sort((a, b) =>
      b.submission.submitted_at.localeCompare(a.submission.submitted_at)
    )
    const newest = sorted[0]!

    return {
      representative: newest.spot,
      meta: {
        spotIds: sorted.map((m) => m.spot.id),
        count: sorted.length,
        submittedAt: newest.submission.submitted_at,
        submittedBy: newest.submission.submitted_by,
      },
    }
  })

  return { workingDrafts, proposalGroups }
}

/** Applied BEFORE the draft partition, so both sections and their counters reflect it. */
export const filterAgamiList = <
  T extends {
    type: number
    content_media_type?: number
    audio_content_path?: string | null
  },
>(
  spots: T[],
  { type, media }: AgamiListFilters
): T[] =>
  spots.filter(
    (spot) => (type === null || spot.type === type) && (media === null || matchesMediaFilter(spot, media))
  )
